import React, { useContext, useState } from 'react';
import { MainContext } from '../../context/MainContext';

import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';

import DynamicSelect from '../resources/DynamicSelect';
import * as Constants from '../../components/api/constants';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Button from '@mui/material/Button';

const buttonWidth = '100%';

// Light speed / blower levels
const arrayOfData5 = Array.from({ length: 5 }, (_, i) => ({
  id: (i + 1).toString(),
  name: (i + 1).toString(),
}));

// Light colour (1–30)
const lightArrayData = Array.from({ length: 30 }, (_, i) => ({
  id: i + 1,
  name: (i + 1).toString(),
}));

// Light modes
const lightModeData = [
  { id: '1', name: 'WHITE' },
  { id: '2', name: 'UCLR' },
  { id: '3', name: 'FADE' },
  { id: '4', name: 'STEP' },
  { id: '5', name: 'PARTY' },
];

// 12/24 hour
const hourData = [
  { id: '1', name: '12' },
  { id: '2', name: '24' },
];

// Temperature 10.0 → 41.0 step 0.2°C
const temperatureArrayData = [];
let val = 10.0;
let tid = 1;
while (val <= 41.0) {
  temperatureArrayData.push({
    id: tid++,
    name: val.toFixed(1),
  });
  val += 0.2;
}

export default function EEPROMGeneral() {
  const state = useContext(MainContext).state;
  const deviceId = state.selectedDevice;
  const r6 = state.R6.split(',');

  const [selectedValues, setSelectedValues] = useState({});

  const handleSelectChange = (id, value) => {
    console.log(id + " " + value);
    setSelectedValues((prev) => ({ ...prev, [id]: value }));
  };

  const sendCommand = (command, value) => {
    console.log(command + " " + value);
    fetch(Constants.baseURL + Constants.apiSendCommand, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        DeviceId: deviceId,
        Command: command,
        Value: String(value),
      }),
      credentials: 'include',
    })
      .then((response) => {
        if (response.ok) {
          toast.success('Command sent successfully. Refreshing...', {
            position: 'top-right',
            autoClose: 6000,
          });
          setTimeout(() => state.refreshDeviceRF(deviceId), 3000);
          setTimeout(() => state.getUserDeviceRf(deviceId), 8000);
        } else {
          toast.error('Send failed. Please try again', {
            position: 'top-right',
            autoClose: 5000,
          });
        }
      })
      .catch((err) => console.log('request failed', err));
  };

  const onSend = (id) => {
    const value = selectedValues[id];
    if (value === undefined || value === null || value === '') return;

    switch (id) {
      case 0:
        sendCommand('blowerLevel', value);
        break;
      case 1:
        sendCommand('lightBrightness', value);
        break;
      case 2:
        sendCommand('lightColour', value);
        break;
      case 3:
        sendCommand('lightMode', value);
        break;
      case 4:
        sendCommand('lightSpeed', value);
        break;
      case 5:
        sendCommand('hourType', value == 12 ? 0 : (value === 24 ? 1 : 0));
        break;
      case 6:
        sendCommand('setTemperature', value);
        break;
    }
  };

  const currentStates = [
    r6[1],
    r6[2],
    r6[3],
    ['White', 'UCLR', 'FADE', 'STEP', 'PARTY'][Number(r6[4])],
    r6[5],
    r6[9] === '0' ? '24' : '12',
    (r6[8] / 10).toFixed(1) + '°C',
  ];

  const rows = [
    { id: 0, label: 'Blower Level', select: arrayOfData5 },
    { id: 1, label: 'Light Brightness', select: arrayOfData5 },
    { id: 2, label: 'Light Colour', select: lightArrayData },
    { id: 3, label: 'Light Mode', select: lightModeData },
    { id: 4, label: 'Light Speed', select: arrayOfData5 },
    { id: 5, label: '12 / 24 Hours', select: hourData },
    { id: 6, label: 'Set Temperature', select: temperatureArrayData },
  ];

  return (
    <>
      <ToastContainer />

      <Typography
        component="h2"
        variant="h6"
        color="white"
        sx={{ fontWeight: 600, mb: 2 }}
      >
        General
      </Typography>

      <Grid container spacing={2}>
        {rows.map((row, index) => (
          <Grid item xs={12} md={6} lg={6} key={row.id}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.2,
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                {row.label}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  fontSize: 13,
                  color: 'text.disabled',
                }}
              >
                {currentStates[index]}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'stretch',
                  flexDirection: { xs: 'column', md: 'row' },
                  width: '100%',
                  minWidth: 0,
                  '--control-h': { xs: '44px', md: '44px' },
                  '& .MuiInputBase-root': { height: 'var(--control-h)' },
                  '& .MuiSelect-select': {
                    height: 'var(--control-h)',
                    display: 'flex',
                    alignItems: 'center',
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                  '& .MuiFormControl-root': { width: '100%', minWidth: 0 },
                  mt: 1
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <DynamicSelect
                    arrayOfData={row.select}
                    value={row.selectedValue} 
                    onSelectChange={(val) => handleSelectChange(row.id, val)}
                    id={row.id}
                    name=""
                  />
                </Box>

                {selectedValues[row.id] !== undefined && selectedValues[row.id] !== '' && (
                  <Button
                    onClick={() => onSend(row.id)}
                    variant="outlined"
                    size="small"
                    sx={{
                      height: 'var(--control-h)',
                      minHeight: 'var(--control-h)',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'white',
                      px: 1.5,
                      width: { xs: '100%', md: 'auto' },
                      flex: { xs: '0 0 auto', md: '0 0 auto' },
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255,255,255,0.08)',
                      },
                    }}
                  >
                    Send
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </>
  );
}
